/**
 * How an alias's outcome verdict reads in the Payer Mappings table.
 *
 * The sweep writes numbers; this turns them into the sentence that makes a flag
 * actionable — "31% of 42 checks, vs 78% at this clinic" answers "is the
 * mapping wrong, or is this clinic just having a bad month?" without the
 * operator opening anything.
 *
 * Deliberately never says "wrong". A mapping can sit under the bar because its
 * patients' data is bad, and the operator is the one who can tell.
 */
import type { PayerAliasHealth } from '../../shared/payerAlias'

export interface AliasHealthLabel {
  /** Short badge text, or null when there is nothing worth showing. */
  badge: string | null
  /** The numbers behind the badge — shown next to it, and as the title. */
  detail: string | null
  tone: 'warn' | 'quiet' | 'none'
}

const pct = (v: number | null): string | null =>
  v === null ? null : `${Math.round(v * 100)}%`

export function aliasHealthLabel(
  health: PayerAliasHealth | undefined,
): AliasHealthLabel {
  if (!health) return { badge: null, detail: null, tone: 'none' }

  const rate = pct(health.successRate)
  const clinic = pct(health.clinicSuccessRate)

  if (health.state === 'unjudged') {
    // Said plainly rather than left blank: "not enough checks yet" and "we
    // never looked" are different facts, and only one of them is a reason to
    // wait before acting.
    return {
      badge: null,
      detail: health.sample === 0
        ? 'No checks yet'
        : `${health.sample} check${health.sample === 1 ? '' : 's'} so far`,
      tone: 'quiet',
    }
  }

  const base = rate === null
    ? `${health.sample} checks`
    : `${rate} of ${health.sample}${clinic ? ` · clinic ${clinic}` : ''}`

  if (health.state === 'poor') {
    const why = health.topRejection === 'invalid_dob'
      ? ' — the payer is matching a different person'
      : health.topRejection === 'member_not_found'
        ? ' — the payer has not heard of these members'
        : ''
    return { badge: 'Check this mapping', detail: `${base}${why}`, tone: 'warn' }
  }
  return { badge: null, detail: base, tone: 'quiet' }
}
